import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const CONFETTI_COUNT = 40;
const CONFETTI_COLORS = ['#00f0ff', '#bf5af2', '#ff2d55', '#ffd60a', '#30d158', '#fff'];

function ConfettiPiece({ color, style }) {
    return (
        <div
            className="absolute w-2 h-3 rounded-sm pointer-events-none"
            style={{
                backgroundColor: color,
                ...style,
            }}
        />
    );
}

function GameResultOverlay({ result, bet, visible }) {
    const { t } = useTranslation();
    const [show, setShow] = useState(false);

    useEffect(() => {
        if (visible) {
            requestAnimationFrame(() => setShow(true));
        } else {
            setShow(false);
        }
    }, [visible]);

    const confetti = useMemo(() => {
        if (result !== 'win') return [];
        return Array.from({ length: CONFETTI_COUNT }, (_, i) => ({
            id: i,
            color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
            left: Math.random() * 100,
            delay: Math.random() * 0.6,
            duration: 1.2 + Math.random() * 1,
            rotation: Math.random() * 360,
            size: 0.5 + Math.random() * 0.7,
        }));
    }, [result, visible]); // eslint-disable-line

    if (!visible) return null;

    const isWin = result === 'win';
    const isLose = result === 'lose';

    const label = isWin ? t('game.victory') : isLose ? t('game.defeat') : t('game.draw');
    const balanceText = isWin ? `+${bet}¢` : isLose ? `-${bet}¢` : '0¢';
    const balanceColor = isWin ? 'text-green-400' : isLose ? 'text-cyber-pink' : 'text-cyber-dim';

    const bgGradient = isWin
        ? 'from-cyan-900/90 via-emerald-900/80 to-cyan-900/90'
        : isLose
            ? 'from-pink-900/90 via-red-900/80 to-pink-900/90'
            : 'from-gray-800/90 via-gray-700/80 to-gray-800/90';

    const borderColor = isWin
        ? 'border-cyber-cyan shadow-[0_0_30px_rgba(0,240,255,0.4)]'
        : isLose
            ? 'border-cyber-pink shadow-[0_0_30px_rgba(255,45,85,0.4)]'
            : 'border-cyber-dim shadow-[0_0_15px_rgba(128,128,128,0.3)]';

    const glowClass = isWin ? 'glow-cyan' : isLose ? 'glow-pink' : '';

    return (
        <div
            className={`absolute inset-0 z-50 flex items-center justify-center transition-all duration-500
                ${show ? 'opacity-100' : 'opacity-0'}`}
            style={{ pointerEvents: 'none' }}
        >
            {/* Confetti layer */}
            {confetti.map((c) => (
                <ConfettiPiece
                    key={c.id}
                    color={c.color}
                    style={{
                        left: `${c.left}%`,
                        top: '-5%',
                        transform: `scale(${c.size}) rotate(${c.rotation}deg)`,
                        animation: `confetti-fall ${c.duration}s ease-in ${c.delay}s forwards`,
                    }}
                />
            ))}

            {/* Result banner */}
            <div
                className={`bg-gradient-to-r ${bgGradient} border-y ${borderColor}
                    px-3 py-2 sm:px-6 sm:py-5 w-full text-center backdrop-blur-sm
                    transition-all duration-500
                    ${show ? 'scale-100 translate-y-0' : 'scale-95 -translate-y-4'}`}
            >
                <div
                    className={`font-display text-xl sm:text-3xl md:text-4xl lg:text-5xl font-black tracking-widest sm:tracking-[0.2em] leading-tight
                        ${isWin ? 'text-cyber-cyan' : isLose ? 'text-cyber-pink' : 'text-white'}
                        ${glowClass}
                        ${isWin ? 'animate-pulse' : ''}`}
                >
                    {label}
                </div>
                <div className={`font-display text-base sm:text-xl md:text-2xl font-bold mt-0.5 sm:mt-1 ${balanceColor} tracking-wider`}>
                    {balanceText}
                </div>
            </div>
        </div>
    );
}

export default GameResultOverlay;
